import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Lock, Loader2, Users, MessageCircle, Package, Salad, LogOut, Dumbbell, BookOpen, Settings, LayoutDashboard } from "lucide-react";
import { adminApi, setAdminToken, api } from "@/lib/api";
import { AdminSettings } from "@/components/AdminSettings";
import { AdminProducts } from "@/components/admin/AdminProducts";
import { AdminRecipes, AdminExercises } from "@/components/admin/AdminContent";
import { AdminGuides } from "@/components/admin/AdminGuides";
import { AdminBookings } from "@/components/admin/AdminBookings";

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("uurs_admin"));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = sessionStorage.getItem("uurs_admin");
    if (t) setAdminToken(t);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password: password.trim() });
      sessionStorage.setItem("uurs_admin", res.data.token);
      setAdminToken(res.data.token);
      setAuthed(true);
      toast.success("Admin access granted");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Incorrect admin password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("uurs_admin");
    setAdminToken(null);
    setAuthed(false);
  };

  if (!authed) return <AdminLogin password={password} setPassword={setPassword} onSubmit={login} loading={loading} />;
  return <AdminDashboard onLogout={logout} />;
}

const AdminLogin = ({ password, setPassword, onSubmit, loading }) => (
  <div className="h-full flex flex-col items-center justify-center px-7 animate-fade-in">
    <div className="w-16 h-16 rounded-3xl bg-emerald-800 flex items-center justify-center mb-5">
      <ShieldAlert className="w-8 h-8 text-emerald-300" />
    </div>
    <h1 className="text-xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head">Admin Access</h1>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 text-center">Enter the admin password to manage the app</p>
    <form onSubmit={onSubmit} className="w-full mt-6 space-y-3">
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          data-testid="admin-password-input"
          type="password"
          autoComplete="current-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        />
      </div>
      <button
        type="submit"
        data-testid="admin-login-btn"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3.5 rounded-2xl transition-colors disabled:opacity-60"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Unlock Dashboard
      </button>
    </form>
  </div>
);

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "recipes", label: "Recipes", icon: Salad },
  { id: "exercises", label: "Exercises", icon: Dumbbell },
  { id: "guides", label: "Guidance", icon: BookOpen },
  { id: "bookings", label: "Bookings", icon: Users },
  { id: "settings", label: "Settings", icon: Settings },
];

const AdminDashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [guides, setGuides] = useState([]);

  const load = async () => {
    try {
      const [m, p, l, e, r, g] = await Promise.all([
        adminApi.get("/admin/metrics"), api.get("/products"), adminApi.get("/leads"), api.get("/exercises"), api.get("/recipes"), api.get("/guides"),
      ]);
      setMetrics(m.data); setProducts(p.data); setLeads(l.data); setExercises(e.data); setRecipes(r.data); setGuides(g.data);
    } catch {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => { load(); }, []);

  const overview = [
    { id: "products", label: "Products", value: products.length || metrics?.total_products, icon: Package, color: "text-blue-500" },
    { id: "recipes", label: "Recipes", value: recipes.length || metrics?.total_recipes, icon: Salad, color: "text-emerald-500" },
    { id: "exercises", label: "Exercises", value: exercises.length || metrics?.total_exercises, icon: Dumbbell, color: "text-amber-600" },
    { id: "guides", label: "Guidance Articles", value: guides.length || metrics?.total_guides, icon: BookOpen, color: "text-violet-500" },
    { id: "bookings", label: "Consultation Requests", value: leads.length || metrics?.total_leads, icon: Users, color: "text-rose-500" },
    { id: "bookings", label: "New Inquiries", value: leads.filter((l) => l.status === "New").length, icon: MessageCircle, color: "text-emerald-600" },
  ];

  return (
    <div className="animate-fade-in pb-6" data-testid="admin-dashboard">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between">
        <div>
          <span className="text-[11px] font-semibold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">Control Center</span>
          <h1 className="text-2xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head">Admin Dashboard</h1>
        </div>
        <button data-testid="admin-logout-btn" onClick={onLogout} className="w-10 h-10 rounded-xl bg-white dark:bg-emerald-900 border border-slate-100 dark:border-emerald-800 flex items-center justify-center text-slate-500">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto scrollbar-none px-4 pb-2">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${tab === t.id ? "bg-emerald-700 dark:bg-emerald-500 text-white dark:text-emerald-950 border-emerald-700 dark:border-emerald-500" : "bg-white dark:bg-emerald-900/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="px-4 mt-2 grid grid-cols-2 gap-3" data-testid="admin-overview">
          {overview.map((m) => (
            <button key={m.label} data-testid={`admin-metric-${m.label}`} onClick={() => setTab(m.id)} className="text-left p-4 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 active:scale-[0.98] transition">
              <m.icon className={`w-5 h-5 ${m.color}`} />
              <p className="text-2xl font-extrabold text-emerald-950 dark:text-emerald-50 mt-2 font-head">{m.value ?? "–"}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{m.label}</p>
            </button>
          ))}
          <p className="col-span-2 text-[11px] text-slate-400 px-1 mt-1">Tap any card to manage that section. Registered users: {metrics?.total_users ?? "–"}.</p>
        </div>
      )}
      {tab === "products" && <AdminProducts products={products} setProducts={setProducts} />}
      {tab === "recipes" && <AdminRecipes recipes={recipes} setRecipes={setRecipes} />}
      {tab === "exercises" && <AdminExercises exercises={exercises} setExercises={setExercises} />}
      {tab === "guides" && <AdminGuides guides={guides} setGuides={setGuides} />}
      {tab === "bookings" && <AdminBookings leads={leads} setLeads={setLeads} />}
      {tab === "settings" && <AdminSettings />}
    </div>
  );
};
